import { StyleProps, chakra } from '@chakra-ui/react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import type { ServerError, UseEnhancedFormProps } from '../../hooks/useEnhancedForm';
import { useEnhancedForm } from '../../hooks/useEnhancedForm';
import { SubmitErrorHandler } from 'react-hook-form/dist/types/form';

export type FormProps<T extends FieldValues> = UseEnhancedFormProps<T> & {
  children: ((methods: UseFormReturn<T, object>) => React.ReactNode) | React.ReactNode;
  id?: string;
  noValidate?: boolean;
  onSubmit: (values: T, methods: UseFormReturn<T, object>) => void | Promise<void>;
  onInvalid?: SubmitErrorHandler<T>;
  serverError?: ServerError;
} & StyleProps;

export function Form<T extends FieldValues>({
  children,
  context,
  id,
  initialValues,
  noValidate = true,
  onSubmit,
  onInvalid,
  schema,
  serverError,
  ...boxProps
}: FormProps<T>) {
  const methods = useEnhancedForm({
    context,
    initialValues,
    mode: 'onBlur',
    schema,
    serverError,
  });

  return (
    <FormProvider {...methods}>
      <chakra.form noValidate={noValidate} id={id} onSubmit={methods.handleSubmit(values => onSubmit(values, methods), onInvalid)} {...boxProps}>
        {typeof children === 'function' ? children(methods) : children}
      </chakra.form>
    </FormProvider>
  );
}
